import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { PowerLevelInline } from '../components/PowerLevelBadge';
import { StreakFire } from '../components/TrophyCase';
import { clubsAPI } from '../lib/api';
import { getInitials } from '../lib/utils';
import { Trophy, Medal, Crown, Zap, Target, Users, ChevronLeft, BarChart3 } from 'lucide-react';

export default function ClubLeaderboard() {
  const { clubId } = useParams();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [club, setClub] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clubRes, lbRes] = await Promise.all([
          clubsAPI.getById(clubId),
          clubsAPI.getLeaderboard(clubId),
        ]);
        setClub(clubRes.data);
        setLeaderboard(lbRes.data || []);
      } catch { setLeaderboard([]); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [clubId]);

  if (loading) return <Layout><div className="space-y-6"><Skeleton className="h-8 w-64" /><Skeleton className="h-[400px]" /></div></Layout>;

  // Awards from leaderboard data
  const topWinner = leaderboard.reduce((best, p) => (!best || p.matches_won > best.matches_won) ? p : best, null);
  const topWinRate = leaderboard.filter(p => p.matches_played >= 3).reduce((best, p) => (!best || p.win_rate > best.win_rate) ? p : best, null);
  const topStreak = leaderboard.reduce((best, p) => (!best || p.best_streak > best.best_streak) ? p : best, null);

  return (
    <Layout>
      <div className="space-y-6" data-testid="club-leaderboard-page">
        <div className="flex items-center gap-3">
          <Link to="/clubs"><Button variant="ghost" size="icon"><ChevronLeft className="h-4 w-4" /></Button></Link>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">{club?.name} Leaderboard</h1>
            <p className="text-sm text-muted-foreground">{leaderboard.length} members ranked</p>
          </div>
        </div>

        {/* Season Awards */}
        {leaderboard.length >= 2 && (
          <div className="grid grid-cols-3 gap-3">
            {topWinner && (
              <Card className="border-yellow-500/30 bg-yellow-500/5">
                <CardContent className="p-4 text-center">
                  <Trophy className="h-6 w-6 text-yellow-500 mx-auto mb-1" />
                  <p className="text-[10px] uppercase text-muted-foreground font-bold tracking-widest">Most Wins</p>
                  <p className="font-heading font-bold mt-1">{topWinner.name}</p>
                  <p className="text-xs text-muted-foreground">{topWinner.matches_won} wins</p>
                </CardContent>
              </Card>
            )}
            {topWinRate && (
              <Card className="border-green-500/30 bg-green-500/5">
                <CardContent className="p-4 text-center">
                  <Target className="h-6 w-6 text-green-500 mx-auto mb-1" />
                  <p className="text-[10px] uppercase text-muted-foreground font-bold tracking-widest">Best Win Rate</p>
                  <p className="font-heading font-bold mt-1">{topWinRate.name}</p>
                  <p className="text-xs text-muted-foreground">{topWinRate.win_rate}%</p>
                </CardContent>
              </Card>
            )}
            {topStreak && topStreak.best_streak > 0 && (
              <Card className="border-orange-500/30 bg-orange-500/5">
                <CardContent className="p-4 text-center">
                  <Zap className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                  <p className="text-[10px] uppercase text-muted-foreground font-bold tracking-widest">Best Streak</p>
                  <p className="font-heading font-bold mt-1">{topStreak.name}</p>
                  <p className="text-xs text-muted-foreground">{topStreak.best_streak} wins</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Leaderboard Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase tracking-tight text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-40" />
                <p>No members ranked yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((player, idx) => (
                  <Link key={player.user_id} to={`/players/${player.user_id}`}>
                    <div className={`flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-muted ${idx < 3 ? 'bg-muted' : 'bg-muted/30'}`}
                      style={idx < 3 ? { borderLeft: `3px solid ${idx === 0 ? '#EAB308' : idx === 1 ? '#9CA3AF' : '#F97316'}` } : {}}
                      data-testid={`lb-rank-${idx + 1}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                          idx === 0 ? 'bg-yellow-500 text-black' : idx === 1 ? 'bg-gray-400 text-black' : idx === 2 ? 'bg-orange-500 text-white' : 'bg-muted-foreground/20'
                        }`}>{idx + 1}</div>
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={player.picture} />
                          <AvatarFallback className="text-xs">{getInitials(player.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="font-heading font-bold text-sm">{player.name}</p>
                            <StreakFire currentStreak={player.current_streak || 0} />
                          </div>
                          <p className="text-xs text-muted-foreground">{player.matches_played} played / {player.win_rate}% win rate</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <PowerLevelInline elo={player.power_level || 1200} />
                        <p className="text-xs text-muted-foreground">{player.matches_won}W - {player.matches_played - player.matches_won}L</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
